import { Outlet } from "react-router-dom";
import { Suspense, useEffect, useRef } from "react";
import { motion, useMotionValue, animate, type PanInfo } from "framer-motion";
import { Skeleton } from "@/components/Skeleton";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ToastViewport } from "@/components/Toast";
import { BottomNav } from "@/components/BottomNav";
import { RequireAuth } from "@/routes/RequireAuth";
import { ShibaTeacher } from "@/features/shiba-teacher";
import { CoachSheet } from "@/features/coach";
import { useUiStore } from "@/stores/uiStore";
import { useHydrateUserSummary } from "@/api/queries/me";
import {
  useCoachFabStore,
  resolveSnap,
  FAB_ICON_SIZE,
  FAB_EDGE_MARGIN,
  FAB_NAV_H,
  FAB_TOP_INSET,
} from "@/stores/coachFabStore";
import { ScrollToTop } from "@/components/ScrollToTop";

function clampTop(top: number, vh: number): number {
  const maxTop = Math.max(FAB_TOP_INSET, vh - FAB_NAV_H - FAB_ICON_SIZE);
  return Math.min(Math.max(top, FAB_TOP_INSET), maxTop);
}

function CoachFab() {
  const setCoachOpen = useUiStore((s) => s.setCoachOpen);
  const side = useCoachFabStore((s) => s.side);
  const offsetYRatio = useCoachFabStore((s) => s.offsetYRatio);
  const setPosition = useCoachFabStore((s) => s.setPosition);

  const draggingRef = useRef(false);

  const x = useMotionValue(
    side === "left"
      ? FAB_EDGE_MARGIN
      : (typeof window !== "undefined" ? window.innerWidth : 375) -
          FAB_ICON_SIZE -
          FAB_EDGE_MARGIN,
  );
  const y = useMotionValue(
    clampTop(
      offsetYRatio * (typeof window !== "undefined" ? window.innerHeight : 667),
      typeof window !== "undefined" ? window.innerHeight : 667,
    ),
  );

  // Place on mount, when the store changes, and on viewport resize/rotate.
  useEffect(() => {
    const place = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      x.set(side === "left" ? FAB_EDGE_MARGIN : vw - FAB_ICON_SIZE - FAB_EDGE_MARGIN);
      y.set(clampTop(offsetYRatio * vh, vh));
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [side, offsetYRatio, x, y]);

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    _info: PanInfo,
  ) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const centerX = x.get() + FAB_ICON_SIZE / 2;
    const topY = y.get();
    const snap = resolveSnap(centerX, topY, {
      width: vw,
      height: vh,
      navH: FAB_NAV_H,
      iconH: FAB_ICON_SIZE,
      topInset: FAB_TOP_INSET,
    });
    const targetX =
      snap.side === "left" ? FAB_EDGE_MARGIN : vw - FAB_ICON_SIZE - FAB_EDGE_MARGIN;
    const targetY = clampTop(snap.offsetYRatio * vh, vh);
    // Persist only after the snap animation finishes. Writing to the store
    // synchronously here would re-run the placement effect, whose x.set()/
    // y.set() cancels this in-flight animation and makes the FAB jump.
    animate(x, targetX, {
      duration: 0.2,
      ease: "easeOut",
      onComplete: () => setPosition(snap.side, snap.offsetYRatio),
    });
    animate(y, targetY, { duration: 0.2, ease: "easeOut" });
    // Release the click-suppression flag AFTER this gesture's trailing click.
    setTimeout(() => {
      draggingRef.current = false;
    }, 0);
  };

  return (
    <motion.button
      type="button"
      drag
      dragMomentum={false}
      dragElastic={0}
      style={{ x, y, position: "fixed", left: 0, top: 0, touchAction: "none" }}
      onDragStart={() => {
        draggingRef.current = true;
      }}
      onDragEnd={handleDragEnd}
      onClick={() => {
        if (draggingRef.current) return;
        setCoachOpen(true);
      }}
      className="z-30 bg-paper rounded-full shadow-card border-2 border-sumi/10 p-2"
      aria-label="打开小柴老师"
    >
      <ShibaTeacher expression="happy" size={48} />
    </motion.button>
  );
}

export function AppLayout() {
  useHydrateUserSummary();
  return (
    <RequireAuth>
      <ErrorBoundary>
        <ScrollToTop />
        <div className="min-h-dvh flex flex-col bg-cream">
          <Suspense
            fallback={
              <div className="flex-1 p-6 flex flex-col gap-4">
                <Skeleton className="h-8 w-2/3" />
                <Skeleton className="h-32 w-full" />
              </div>
            }
          >
            <main className="flex-1 pb-4 animate-fade-in-up">
              <Outlet />
            </main>
          </Suspense>
          <CoachFab />
          <CoachSheet />
          <BottomNav />
          <ToastViewport />
        </div>
      </ErrorBoundary>
    </RequireAuth>
  );
}
